/** @jsxRuntime classic */
/** @jsx jsx */
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";
import { gql } from "@apollo/client";
import Modal from "react-modal";
import {
  jsx,
  Box,
  Heading,
  ThemeProvider,
  Container,
  Button,
  Flex,
  Paragraph,
  Avatar,
  Image,
  Badge,
  AspectRatio,
  Close,
  Grid,
} from "theme-ui";
import CommonHead from "components/common-head";
import CommonPostBody from "components/common-post-body";
import SecondaryLayout from "components/secondary-layout";

import SEO from "components/seo";
import { GetStaticPropsContext } from "next";
import client from "services/apollo";
import { appTheme } from "theme";
import { Instructor, ProgramOutline, PROGRAM_OUTLINE_FIELDS } from "types";
import { enumToReadable } from "helpers/strings";
import {
  freeKlasses,
  instructorName,
  klassesMins,
  programHourMins,
  programNKlasses,
  videoLength,
} from "helpers/programs";
import pluralize from "pluralize";
import { Link } from "components/link";
import { useModalStyle } from "helpers/modals";
import {
  FaCheck,
  FaChevronCircleDown,
  FaChevronCircleUp,
  FaChevronDown,
  FaChevronUp,
  FaCircle,
  FaPlay,
  FaPlayCircle,
} from "react-icons/fa";
import AbsoluteOverlay from "components/absolute-overlay";
import DownloadAppCTA from "components/download-app-cta";
import ClickLink from "components/ClickLink";

// We use static instead of SSR so it's faster.

export const getStaticPaths = async () => {
  const { data } = await client.query({
    query: gql`
      query programOutlines {
        programOutlines {
          slug
        }
      }
    `,
  });

  return {
    paths: data.programOutlines.map((p: Pick<ProgramOutline, "slug">) => ({
      params: { slug: p.slug },
    })),
    fallback: "blocking",
  };
};

export const getStaticProps = async (context: GetStaticPropsContext) => {
  const { data } = await client.query({
    query: gql`
      ${PROGRAM_OUTLINE_FIELDS}
      query programOutline($slug: String!) {
        programOutline(slug: $slug) {
          ...ProgramOutlineFields
        }
      }
    `,
    variables: {
      slug: context.params?.slug,
    },
  });
  return {
    props: {
      outline: data.programOutline,
    },
  };
};

const PreviewModal = ({
  outline,
  isOpen,
  toggle,
  selectedKlassId,
}: {
  outline: ProgramOutline;
  isOpen: boolean;
  toggle: () => void;
  selectedKlassId?: string;
}) => {
  const modalStyle = useModalStyle();
  const klasses = useMemo(() => freeKlasses(outline), [outline]);
  const [curIdx, setCurIdx] = useState(0);
  const curKlass = klasses[curIdx];

  useEffect(() => {
    if (selectedKlassId === undefined) {
      setCurIdx(0);
      return;
    }

    const selectedIdx = klasses.findIndex((k) => k.id === selectedKlassId);
    if (selectedIdx !== -1) setCurIdx(selectedIdx);
  }, [klasses, selectedKlassId]);

  useEffect(() => {
    Modal.setAppElement("#root");
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
      onRequestClose={toggle}
      style={{
        ...modalStyle,
        content: {
          ...modalStyle.content,
          width: "100%",
          maxWidth: 600,
          backgroundColor: "black",
        },
        overlay: {
          backgroundColor: "rgba(0,0,0,0.5)",
        },
      }}
    >
      <Flex p={4} sx={{ flexDirection: "row" }}>
        <Box sx={{ flex: 1 }}>
          <Heading as="h5" color="#ddd">
            Program Preview
          </Heading>
          <Heading as="h3" color="white">
            {outline.name}
          </Heading>
        </Box>
        <Close color="white" onClick={toggle} />
      </Flex>
      <AspectRatio
        ratio={16 / 9}
        sx={{
          width: "100%",
          backgroundColor: "black",
          alignItems: "center",
          justifyContent: "center",
          display: "flex",
        }}
      >
        <video
          controls
          autoPlay
          width="100%"
          onEnded={() => {
            setCurIdx((cur) => (cur === klasses.length - 1 ? cur : cur + 1));
          }}
          // Necessary to update this on new curKlass.
          key={curKlass.view0.downloadURI}
        >
          <source src={curKlass.view0.downloadURI}></source>
        </video>
      </AspectRatio>
      <Heading as="h5" p={2} color="#ddd">
        Free Sample Classes
      </Heading>
      {klasses.map((k, idx) => {
        const selected = curKlass.id === k.id;
        return (
          <Flex
            key={k.id}
            sx={{
              flexDirection: "row",
              alignItems: "center",
              backgroundColor: selected ? "#444" : undefined,
              cursor: "pointer",
            }}
            p={2}
            onClick={() => {
              setCurIdx(idx);
            }}
          >
            <Image src={k.imageURI} width="64" mr={2}></Image>
            <Flex sx={{ flexDirection: "row", flex: 1, alignItems: "center" }}>
              {selected && (
                <Box mx={1}>
                  <FaPlayCircle color="ddd" />
                </Box>
              )}
              <Heading as="h5" color="white">
                {k.name}
              </Heading>
            </Flex>
            <Paragraph
              sx={{
                ml: 3,
                fontFamily: "sans-serif",
                fontVariant: "tabular-nums",
                display: ["none", null, null, null, "block"],
                fontWeight: "bold",
                fontSize: 14,
                color: "#ddd",
              }}
            >
              {videoLength(k.view0.lengthSeconds)}
            </Paragraph>
          </Flex>
        );
      })}
    </Modal>
  );
};

const CoachSection = ({ instructor }: { instructor: Instructor }) => {
  return (
    <Box id="coach" my={3}>
      <Heading as="h2">Coach</Heading>
      <Flex sx={{ flexDirection: "row", my: 3 }}>
        <Avatar src={instructor.avatarURI} size={120} mr={3} />
        <Box>
          <Heading as="h3">{instructorName(instructor)}</Heading>
          {/* TODO: change once we add more instructors */}
          <Heading as="h5">{instructor.tagLine}</Heading>
        </Box>
      </Flex>
      <Paragraph>{instructor.bio}</Paragraph>
    </Box>
  );
};

const HeaderSection = ({
  toggleModal,
  outline,
}: {
  toggleModal: () => void;
  outline: ProgramOutline;
}) => {
  return (
    <Flex
      my={5}
      sx={{
        flexDirection: ["column", null, null, "row"],
      }}
    >
      <Box
        sx={{
          width: ["100%", null, null, "50%"],
          maxWidth: [null, null, null, 400],
          mr: [0, 10, 20],
        }}
      >
        <Box
          sx={{
            position: "relative",
            cursor: "pointer",
          }}
          onClick={toggleModal}
        >
          <AbsoluteOverlay
            style={{
              background:
                "linear-gradient(180deg,rgba(28,29,31,0) 0%,rgba(28,29,31,0.9) 100%)",
            }}
          ></AbsoluteOverlay>
          <AbsoluteOverlay
            style={{
              alignItems: "flex-end",
            }}
          >
            <Heading
              as="h6"
              sx={{
                color: "white",
                fontSize: 16,
                paddingBottom: 16,
              }}
            >
              Preview this program
            </Heading>
          </AbsoluteOverlay>

          <AbsoluteOverlay>
            <FaCircle color="white" size={50} />
          </AbsoluteOverlay>
          <AbsoluteOverlay>
            <FaPlay color="black" size={25} style={{ marginLeft: 5 }} />
          </AbsoluteOverlay>

          <Image src={outline.imageURI} />
        </Box>
      </Box>

      <Box sx={{ flex: 1, my: 1 }}>
        <Badge>{enumToReadable(outline.sport)}</Badge>
        <Heading as="h1" variant="largeH1">
          {outline.name}
        </Heading>
        <Heading as="h4" my={3}>
          {outline.shortDesc}
        </Heading>
        <Paragraph>
          Taught by{" "}
          <Link path="#coach">{instructorName(outline.instructor)}</Link>
        </Paragraph>
        <Box my={2}>
          <Link path="/download">
            <Button>Access Full Program on the App</Button>
          </Link>
        </Box>
      </Box>
    </Flex>
  );
};

const LearnSection = ({ outline }: { outline: ProgramOutline }) => {
  return (
    <Box
      my={3}
      p={3}
      sx={{
        borderColor: "border_color",
        borderWidth: 1,
        borderStyle: "solid",
        borderRadius: 10,
      }}
    >
      <Heading as="h2">What you'll learn</Heading>
      <Grid
        my={3}
        gap={5}
        sx={{
          gridTemplateColumns: ["repeat(1,1fr)", "repeat(2,1fr)", null],
        }}
      >
        {outline.learn.map((l) => (
          <Flex sx={{ flexDirection: "row", alignItems: "center" }} key={l}>
            <Box mr={4}>
              <FaCheck size={15} />
            </Box>
            <Paragraph sx={{ flex: 1 }}>{l}</Paragraph>
          </Flex>
        ))}
      </Grid>
    </Box>
  );
};

const DescSection = ({ outline }: { outline: ProgramOutline }) => {
  return (
    <Box>
      <Box my={3}>
        <Heading as="h2">What you'll need</Heading>
        <Box my={4}>
          <ul>
            {outline.needs.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </Box>
      </Box>
      <Box my={3}>
        <Heading as="h2">Description</Heading>
        <Paragraph my={4}>{outline.desc}</Paragraph>
      </Box>
    </Box>
  );
};

const AccordionItem = ({
  header,
  children,
  collapsed,
}: {
  header: ReactNode;
  children: ReactNode;
  collapsed: boolean;
}) => {
  const [curCollapsed, setCurCollapsed] = useState(collapsed);

  useEffect(() => {
    setCurCollapsed(collapsed);
  }, [collapsed]);

  return (
    <Box
      sx={{
        borderColor: "border_color",
        borderWidth: 1,
        borderBottomWidth: 0,
        borderStyle: "solid",
      }}
    >
      {/* Header */}
      <Flex
        sx={{
          backgroundColor: "background_secondary",
          flexDirection: "row",
          alignItems: "center",
          borderColor: "border_color",
          borderWidth: 0,
          borderBottomWidth: curCollapsed ? 0 : 1,
          borderStyle: "solid",
          cursor: "pointer",
          px: 5,
          py: 2,
        }}
        onClick={() => setCurCollapsed((v) => !v)}
      >
        <Box mr={5}>
          {curCollapsed ? (
            <FaChevronDown size={14} />
          ) : (
            <FaChevronUp size={14} />
          )}
        </Box>
        <Box sx={{ flex: 1 }}>{header}</Box>
      </Flex>
      {/* Body */}
      <Box sx={{ display: curCollapsed ? "none" : "inherit" }}>{children}</Box>
    </Box>
  );
};

const StagesSection = ({
  toggleModal,
  outline,
  setSelectedKlassId,
}: {
  toggleModal: () => void;
  outline: ProgramOutline;
  setSelectedKlassId: Dispatch<SetStateAction<string | undefined>>;
}) => {
  const [stagesCollapsed, setStagesCollapsed] = useState(
    outline.stages.map((_v, idx) => idx !== 0)
  );
  const [collapseAll, setCollapseAll] = useState(false);

  const toggleStagesCollapse = () => {
    setStagesCollapsed(outline.stages.map(() => collapseAll));
    setCollapseAll((v) => !v);
  };

  return (
    <Box
      sx={{
        borderColor: "border_color",
        borderWidth: 0,
        borderBottomWidth: 1,
        borderStyle: "solid",
        my: 3,
      }}
    >
      <Heading as="h2">Program content</Heading>
      <Flex
        sx={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Paragraph sx={{ flex: 1 }}>
          {pluralize("section", outline.stages.length, true)}
          {" · "}
          {pluralize("class", programNKlasses(outline), true)}
          {" · "}
          {programHourMins(outline)}
        </Paragraph>
        <ClickLink
          onClick={toggleStagesCollapse}
          style={{ textAlign: "right" }}
        >
          {collapseAll ? "Collapse all sections" : "Expand all sections"}
        </ClickLink>
      </Flex>
      {/* Each stage is an accordion item */}
      {outline.stages.map((stage, idx) => {
        const mins = klassesMins(stage.klasses);
        return (
          <AccordionItem
            key={stage.name}
            header={
              <Flex sx={{ flexDirection: "row", alignItems: "center" }}>
                <Heading as="h4" sx={{ flex: 1 }}>
                  {stage.name}
                </Heading>
                <Paragraph
                  sx={{
                    ml: 3,
                    fontFamily: "sans-serif",
                    fontVariant: "tabular-nums",
                  }}
                >
                  {pluralize("classes", stage.klasses.length, true)}
                  {" · "}
                  {`${mins}min`}
                </Paragraph>
              </Flex>
            }
            collapsed={stagesCollapsed[idx]}
          >
            <Box p={4}>
              {stage.klasses.map((k) => {
                // --- Class row entry ---

                const [descOpen, setDescOpen] = useState(false);

                const selectKlass = () => {
                  // Toggle on/off so we trigger state change in case
                  // we've previously selected the same klass ID, but selected another
                  // klass id inside.
                  setSelectedKlassId(undefined);
                  setSelectedKlassId(k.id);
                  toggleModal();
                };
                return (
                  <Flex
                    sx={{
                      flexDirection: "row",
                      alignItems: "flex-start",
                      justifyContent: "center",
                    }}
                    key={k.id}
                  >
                    <Flex sx={{ flexDirection: "column", flex: 1 }}>
                      <Flex
                        sx={{
                          flexDirection: "row",
                          alignItems: "center",
                        }}
                      >
                        {k.premium ? (
                          <Paragraph>{k.name}</Paragraph>
                        ) : (
                          <ClickLink onClick={selectKlass}>{k.name}</ClickLink>
                        )}
                        <Box
                          onClick={() => setDescOpen((v) => !v)}
                          sx={{ cursor: "pointer", px: 2 }}
                        >
                          {descOpen ? (
                            <FaChevronCircleUp></FaChevronCircleUp>
                          ) : (
                            <FaChevronCircleDown></FaChevronCircleDown>
                          )}
                        </Box>
                      </Flex>
                      <Paragraph
                        sx={{ p: 3, display: descOpen ? "inherit" : "none" }}
                      >
                        {k.desc}
                      </Paragraph>
                    </Flex>
                    {!k.premium && (
                      <ClickLink onClick={selectKlass}>Preview</ClickLink>
                    )}
                    <Paragraph
                      sx={{
                        ml: 3,
                        fontFamily: "sans-serif",
                        fontVariant: "tabular-nums",
                      }}
                    >
                      {videoLength(k.view0.lengthSeconds)}
                    </Paragraph>
                  </Flex>
                );
              })}
            </Box>
          </AccordionItem>
        );
      })}
    </Box>
  );
};

const OutlineSection = ({ outline }: { outline: ProgramOutline }) => {
  const [showModal, setShowModal] = useState(false);
  const toggleModal = () => setShowModal((val) => !val);
  const [selectedKlassId, setSelectedKlassId] = useState<string | undefined>(
    undefined
  );

  return (
    <Flex sx={{ flexDirection: "column", mb: 8 }}>
      <Link path="/app/programs">
        <Button variant="textButton">Back to All Programs</Button>
      </Link>
      <HeaderSection toggleModal={toggleModal} outline={outline} />
      <LearnSection outline={outline} />
      <DescSection outline={outline} />
      <StagesSection
        toggleModal={toggleModal}
        outline={outline}
        setSelectedKlassId={setSelectedKlassId}
      />
      <Flex
        my={2}
        sx={{ flexDirection: "row", justifyContent: "center", width: "100%" }}
      >
        <Link path="/download">
          <Button>View All Classes on the App</Button>
        </Link>
      </Flex>
      <CoachSection instructor={outline.instructor} />

      <PreviewModal
        outline={outline}
        isOpen={showModal}
        toggle={toggleModal}
        selectedKlassId={selectedKlassId}
      ></PreviewModal>
    </Flex>
  );
};

const Program = ({ outline }: { outline: ProgramOutline | null }) => {
  return (
    <ThemeProvider theme={appTheme}>
      <SecondaryLayout>
        <CommonHead />
        <SEO
          title={
            outline === null
              ? `Program Not Found | oneCoach`
              : `${outline.name} | oneCoach`
          }
          description={
            outline === null
              ? "The oneCoach program you're looking for could not be found!"
              : `oneCoach's "${outline.name}" ${enumToReadable(
                  outline.sport
                )} program for ${enumToReadable(
                  outline.skillLevel
                )} players consists of ${pluralize(
                  "online video class",
                  programNKlasses(outline),
                  true
                )} designed to take you to the next level.`
          }
        />
        <div id="root">
          <CommonPostBody />
          <section sx={{ variant: "section.general" }}>
            <Container>
              {outline === null ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    my: 8,
                  }}
                >
                  <Box sx={{ my: 2 }}>
                    <Heading as="h1" sx={{ textAlign: "center" }}>
                      Program could not be found.
                    </Heading>
                    <Heading as="h3" sx={{ textAlign: "center" }}>
                      Did you misspell the link 🤔?
                    </Heading>
                  </Box>
                  <Link path="/app/programs">
                    <Button>Return to All Programs</Button>
                  </Link>
                </Box>
              ) : (
                <OutlineSection outline={outline} />
              )}
            </Container>
          </section>
          <DownloadAppCTA />
        </div>
      </SecondaryLayout>
    </ThemeProvider>
  );
};

export default Program;
